import { ArrowLeftRight, CheckCircle2, FileUp, MapPin, MessageSquare, PiggyBank, PhoneCall, UserCheck, Wallet, XCircle } from 'lucide-react'

import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Chip } from '@/components/ui/chip'
import { Skeleton } from '@/components/ui/skeleton'

import { bucketLabel, date, dateTime, inrFull, relativeTime } from '@/lib/format'
import { useLabel } from '@/lib/labels'

import { useActivity, type ActivityItem, type ActivityKind } from './api'

/**
 * The merged operational timeline for a case, newest first. Auto-refreshes
 * every 5s (same cadence as the queue) so two agents working the same case
 * see each other's actions in near-real-time.
 *
 * Kinds: dispositions, PTPs, allocation changes (assignment / reassignment),
 * case status changes from the audit chain, and cash receipts. The filter
 * row narrows to one kind — a case with months of contact history otherwise
 * buries the two allocation events someone is actually looking for.
 */
const FILTERS: { value: ActivityKind | 'ALL'; label: string }[] = [
  { value: 'ALL',         label: 'All' },
  { value: 'ALLOCATION',  label: 'Assignment' },
  { value: 'CASE_STATUS', label: 'Status' },
  { value: 'DISPOSITION', label: 'Contact' },
  { value: 'PTP',         label: 'PTP' },
  { value: 'RECEIPT',     label: 'Cash' },
  { value: 'PAYMENT',     label: 'Digital' },
]

export function ActivityTimeline({ caseId }: { caseId: string }) {
  const { data, isPending, isError } = useActivity(caseId)
  const [filter, setFilter] = useState<ActivityKind | 'ALL'>('ALL')

  const items = useMemo(
    () => (!data ? [] : filter === 'ALL' ? data : data.filter((i: ActivityItem) => i.kind === filter)),
    [data, filter],
  )

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Activity</CardTitle>
            <CardDescription>
              Assignment, status, contact, PTPs and payments · auto-refresh 5s.
            </CardDescription>
          </div>
          {data && <Badge tone="navy">{items.length}</Badge>}
        </div>
        {data && data.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {FILTERS.map((f) => {
              const n = f.value === 'ALL'
                ? data.length
                : data.filter((i: ActivityItem) => i.kind === f.value).length
              // Hide kinds this case has none of — an empty chip is noise.
              if (n === 0 && f.value !== 'ALL') return null
              return (
                <Chip
                  key={f.value}
                  active={filter === f.value}
                  onClick={() => setFilter(f.value)}
                >
                  {f.label} ({n})
                </Chip>
              )
            })}
          </div>
        )}
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {isPending && (
          <div className="space-y-2 px-5 pb-4">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        )}
        {isError && (
          <div className="px-5 pb-4 text-sm text-negative">
            Couldn't load activity.
          </div>
        )}
        {data && items.length === 0 && (
          <div className="px-5 pb-4 text-center">
            <p className="text-sm font-medium text-navy-900">
              {data.length === 0 ? 'No activity yet' : 'Nothing of this kind'}
            </p>
            <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
              {data.length === 0
                ? 'Capture a disposition on the left. It will appear here within seconds.'
                : 'Try a different filter.'}
            </p>
          </div>
        )}
        {items.length > 0 && (
          <ol className="relative">
            {items.map((item: ActivityItem, i: number) => (
              <Item key={item.id} item={item} last={i === items.length - 1} />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  )
}

function Item({ item, last }: { item: ActivityItem; last: boolean }) {
  const label = useLabel()
  return (
    <li className="relative px-5 py-3">
      {/* Rail */}
      {!last && <span aria-hidden className="absolute left-[2.4rem] top-9 bottom-0 w-px bg-slate-200" />}

      <div className="flex items-start gap-3">
        <Glyph kind={glyphFor(item)} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-medium text-navy-900">{renderTitle(item)}</span>
            {item.channel && <Badge tone="slate">{label('channel', item.channel)}</Badge>}
            {item.kind === 'PTP' && item.ptp?.status && <PtpStatusBadge status={item.ptp.status} />}
            {item.kind === 'ALLOCATION' && item.allocation && (
              <Badge tone="slate">{item.allocation.slot}</Badge>
            )}
            {isCollection(item) && item.receipt?.status && (
              <Badge tone={collectionTone(item.receipt.status)}>{item.receipt.status}</Badge>
            )}
          </div>

          <Subline item={item} />

          <p className="mt-1 text-2xs uppercase tracking-wider text-muted-foreground">
            {/* On an assignment the actor is the manager who handed the case
                out, and the subline right above shows the agent who received
                it. Two bare emp ids stacked like that read as interchangeable,
                so this one says which role it is. */}
            {actorLabel(item)}
            <span aria-hidden> · </span>
            <span title={dateTime(item.at)}>{relativeTime(item.at)}</span>
          </p>
        </div>
      </div>
    </li>
  )
}

/**
 * Who performed the activity. Allocations name the role explicitly because the
 * row already carries a second emp id (the agent) one line up.
 *
 * An ENDED allocation carries no actor — `allocations` records who granted it,
 * not who revoked it — so it stays a plain dash rather than claiming the
 * assigner also ended it.
 */
function actorLabel(item: ActivityItem): string {
  if (!item.actorEmpId) return '—'
  if (item.kind === 'ALLOCATION' && item.allocation?.event === 'ASSIGNED') {
    return `Assigned by ${item.actorEmpId}`
  }
  return item.actorEmpId
}

function Subline({ item }: { item: ActivityItem }) {
  const label = useLabel()
  if (item.kind === 'DISPOSITION' && item.disposition?.notes) {
    return <p className="mt-1 text-xs leading-snug text-navy-800/90">{item.disposition.notes}</p>
  }
  if (item.kind === 'PTP' && item.ptp) {
    return (
      <p className="mt-1 text-xs text-navy-800">
        <span className="font-mono tabular-nums">{inrFull(item.ptp.promisedAmount)}</span>
        <span className="text-muted-foreground"> due </span>
        <span className="tabular-nums">{date(item.ptp.promisedDate)}</span>
        {item.ptp.graceDays > 0 && (
          <span className="text-muted-foreground"> · {item.ptp.graceDays}d grace</span>
        )}
        {item.ptp.keptViaLateData && (
          <Badge tone="positive" className="ml-2">Kept via late data</Badge>
        )}
        <PtpLocation ptp={item.ptp} />
      </p>
    )
  }
  if (item.kind === 'ALLOCATION' && item.allocation) {
    const a = item.allocation
    return (
      <p className="mt-1 text-xs text-navy-800">
        {a.agentName ?? a.agentEmpId ?? 'Unknown agent'}
        {a.agentEmpId && <span className="text-muted-foreground"> · {a.agentEmpId}</span>}
        {a.event === 'ENDED' && a.endReason && (
          <span className="text-muted-foreground"> · {a.endReason}</span>
        )}
      </p>
    )
  }
  if (item.kind === 'CASE_STATUS' && item.caseStatus) {
    const c = item.caseStatus
    return (
      <p className="mt-1 text-xs text-navy-800">
        {c.dpd != null && <span className="tabular-nums">DPD {c.dpd}</span>}
        {c.bucket && <span className="text-muted-foreground"> · {bucketLabel(c.bucket)}</span>}
        {c.outstanding && (
          <span className="text-muted-foreground">
            {' '}· <span className="tabular-nums">{inrFull(c.outstanding)}</span> outstanding
          </span>
        )}
        {/* The "why did this move overnight?" answer. */}
        {c.sourceBatchId && (
          <Link
            to="/ingest/$batchId"
            params={{ batchId: c.sourceBatchId }}
            className="ml-2 font-mono text-2xs underline-offset-4 hover:underline"
          >
            from upload {c.sourceBatchId.slice(0, 8)}…
          </Link>
        )}
      </p>
    )
  }
  if (isCollection(item) && item.receipt) {
    return (
      <p className="mt-1 text-xs text-navy-800">
        <span className="font-mono tabular-nums">{inrFull(item.receipt.amount)}</span>
        <span className="text-muted-foreground"> · {label('paymentMode', item.receipt.paymentMode)}</span>
      </p>
    )
  }
  return null
}

/**
 * Where the field app took the promise, as a map link. Tele and web PTPs
 * carry no fix, so they show nothing rather than "not captured".
 */
function PtpLocation({ ptp }: { ptp: NonNullable<ActivityItem['ptp']> }) {
  const lat = ptp.geoLat != null ? Number(ptp.geoLat) : NaN
  const lng = ptp.geoLng != null ? Number(ptp.geoLng) : NaN
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  const acc = ptp.geoAccuracyM != null ? Number(ptp.geoAccuracyM) : NaN
  return (
    <span className="mt-1 flex flex-wrap items-center gap-2">
      <a
        className="inline-flex items-center gap-1 text-navy-700 underline-offset-4 hover:underline"
        href={`https://www.google.com/maps?q=${lat},${lng}`}
        target="_blank"
        rel="noreferrer"
      >
        <MapPin className="h-3 w-3" aria-hidden />
        <span className="tabular-nums">
          {lat.toFixed(5)}, {lng.toFixed(5)}
          {Number.isFinite(acc) && ` (±${Math.round(acc)} m)`}
        </span>
      </a>
      {ptp.mockLocationDetected && <Badge tone="warning">Mock location</Badge>}
    </span>
  )
}

/** Cash and digital carry the same payload and render the same way. */
function isCollection(item: ActivityItem): boolean {
  return item.kind === 'RECEIPT' || item.kind === 'PAYMENT'
}

function collectionTone(status: string): 'positive' | 'negative' | 'slate' {
  if (status === 'CONFIRMED') return 'positive'
  if (status === 'FAILED' || status === 'EXPIRED' || status === 'CANCELLED') return 'negative'
  return 'slate'
}

function renderTitle(item: ActivityItem): string {
  switch (item.kind) {
    case 'PTP':
      return renderPtpTitle(item)
    case 'ALLOCATION':
      return item.allocation?.event === 'ENDED'
        ? item.allocation.status === 'TRANSFERRED'
          ? 'Reassigned away'
          : `Allocation ${(item.allocation.status ?? '').toLowerCase() || 'ended'}`
        : 'Assigned'
    case 'CASE_STATUS':
      return caseStatusTitle(item.caseStatus?.eventType)
    case 'RECEIPT':
      return 'Cash collected'
    case 'PAYMENT':
      return 'Paid online'
    default:
      return renderDispoTitle(item)
  }
}

function caseStatusTitle(eventType: string | undefined): string {
  switch (eventType) {
    case 'CASE.OPENED':               return 'Case opened'
    case 'CASE.CURED':                return 'Case cured — closed'
    case 'CASE.UPDATED':              return 'Dues refreshed'
    case 'CASE.PRIORITY_OVERRIDDEN':  return 'Priority changed'
    default:                          return eventType ?? 'Case updated'
  }
}

function glyphFor(item: ActivityItem): GlyphKind {
  switch (item.kind) {
    case 'PTP':         return 'ptp'
    case 'ALLOCATION':  return item.allocation?.event === 'ENDED' ? 'reassigned' : 'assigned'
    case 'CASE_STATUS': return 'status'
    case 'RECEIPT':
    case 'PAYMENT':     return 'receipt'
    default:            return dispositionGlyph(item.disposition?.contactResult)
  }
}

function renderDispoTitle(item: ActivityItem): string {
  if (!item.disposition) return 'Disposition'
  const cr = item.disposition.contactResult.replace(/_/g, ' ').toLowerCase()
  const o = item.disposition.outcome
  return o
    ? `${capitalise(cr)} · ${o.replace(/_/g, ' ').toLowerCase()}`
    : capitalise(cr)
}

function renderPtpTitle(item: ActivityItem): string {
  if (!item.ptp) return 'PTP'
  if (item.ptp.status === 'OPEN') return 'PTP opened'
  if (item.ptp.status === 'KEPT') return 'PTP kept'
  if (item.ptp.status === 'PARTIAL') return 'PTP partial'
  if (item.ptp.status === 'BROKEN') return 'PTP broken'
  if (item.ptp.status === 'CANCELLED') return 'PTP cancelled'
  if (item.ptp.status === 'RE_PROMISED') return 'PTP replaced by a new promise'
  return `PTP ${item.ptp.status.toLowerCase()}`
}

type GlyphKind =
  | 'connected' | 'no-contact' | 'invalid' | 'callback' | 'ptp'
  | 'assigned' | 'reassigned' | 'status' | 'receipt'

function Glyph({ kind }: { kind: GlyphKind }) {
  const map = {
    connected:  { bg: 'bg-positive-soft', fg: 'text-positive', icon: <PhoneCall className="h-3.5 w-3.5" /> },
    'no-contact':{ bg: 'bg-slate-100',    fg: 'text-slate-600', icon: <PhoneCall className="h-3.5 w-3.5" /> },
    invalid:    { bg: 'bg-negative-soft', fg: 'text-negative', icon: <XCircle className="h-3.5 w-3.5" /> },
    callback:   { bg: 'bg-info-soft',     fg: 'text-info',     icon: <MessageSquare className="h-3.5 w-3.5" /> },
    ptp:        { bg: 'bg-navy-100',      fg: 'text-navy-700', icon: <PiggyBank className="h-3.5 w-3.5" /> },
    assigned:   { bg: 'bg-navy-100',      fg: 'text-navy-700', icon: <UserCheck className="h-3.5 w-3.5" /> },
    reassigned: { bg: 'bg-slate-100',     fg: 'text-slate-600', icon: <ArrowLeftRight className="h-3.5 w-3.5" /> },
    status:     { bg: 'bg-info-soft',     fg: 'text-info',     icon: <FileUp className="h-3.5 w-3.5" /> },
    receipt:    { bg: 'bg-positive-soft', fg: 'text-positive', icon: <Wallet className="h-3.5 w-3.5" /> },
  } as const
  const m = map[kind]
  return (
    <span className={`relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-4 ring-white ${m.bg} ${m.fg}`}>
      {m.icon}
    </span>
  )
}

function dispositionGlyph(cr: string | null | undefined): 'connected' | 'no-contact' | 'invalid' | 'callback' {
  if (cr === 'CONNECTED') return 'connected'
  if (cr === 'INVALID_NUMBER' || cr === 'WRONG_NUMBER') return 'invalid'
  if (cr === 'CALLBACK_REQUESTED') return 'callback'
  return 'no-contact'
}

function PtpStatusBadge({ status }: { status: string }) {
  if (status === 'OPEN')      return <Badge tone="info">OPEN</Badge>
  if (status === 'KEPT')      return <Badge tone="positive"><CheckCircle2 className="h-3 w-3" />KEPT</Badge>
  if (status === 'PARTIAL')   return <Badge tone="warning">PARTIAL</Badge>
  if (status === 'BROKEN')    return <Badge tone="negative"><XCircle className="h-3 w-3" />BROKEN</Badge>
  if (status === 'CANCELLED') return <Badge tone="slate">CANCELLED</Badge>
  if (status === 'RE_PROMISED') return <Badge tone="slate">RE-PROMISED</Badge>
  return <Badge tone="slate">{status}</Badge>
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
